/*
 * $Id:: index.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useEffect, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { globals as globalsSlice } from '../../../app/globalsSlice';
import { t } from '../../../providers';
import { registration as regSlice, error as regError, loading as regLoading } from '../authenticationSlice';
import AuthContainer from '../components/AuthContainer';
import RequestResult from '../components/RequestResult';
import { SLICE_NAME } from '../constants';
import StepOne from './components/StepOne';
import StepThree from './components/StepThree';
import StepTwo from './components/StepTwo';

const accountTypes = [
    {
        key: 'company.individual',
        value: 'applicant',
        descriptionItems: ['individual.my.behalf', 'individual.behalf.other'],
        subTypes: [
            { key: 'business.company', value: 'Business/Company' },
            { key: 'individuals', value: 'Individual(s)' },
        ],
    },
    {
        key: 'representative',
        value: 'representative',
        descriptionItems: ['individual.authorized', 'lawyer.behalf.firm', 'lawyer.outside.firm'],
        subTypes: [
            { key: 'employee.representative', value: 'Employee Representative' },
            { key: 'legal.practitioner', value: 'Legal Practitioner' },
            { key: 'euipo.professional.practitioner', value: 'EUIPO Professional Practitioner' },
            { key: 'association', value: 'Association' },
        ],
    },
];

const legalStatusOptions = [
    { key: 'legalEntityStatus', value: 'Business/Company', literal: 'business.company' },
    { key: 'individualStatus', value: 'Individual(s)', literal: 'individuals' },
];

const SelfRegistrationPage = () => {
    const loading = useSelector(regLoading);
    const error = useSelector(regError);
    const registration = useSelector(regSlice);
    const { globals } = useSelector(globalsSlice);

    const { supportedLanguages, account, ipo } = globals;

    const defaultLanguage = (!!supportedLanguages && Object.keys(supportedLanguages)[1]) || 'pt';
    const [step, setStep] = useState('FIRST');
    const [registrationForm, setRegistrationForm] = useState({});
    const [candidateFieldsList, setCandidateFieldsList] = useState([]);

    const history = useHistory();
    const dispatch = useDispatch();

    useEffect(() => {
        if (registration && registration.userName && registration.fromAction === 'validateCredentials') setStep('SECOND');
        if (registration && registration.userName && registration.fromAction === 'registerAccount') setStep('SUCCESS');
    }, [registration]);

    useEffect(() => {
        return () => {
            dispatch({ type: `${SLICE_NAME}/registration`, payload: null });
            dispatch({ type: `${SLICE_NAME}/error`, payload: null });
        };
    }, [dispatch]);

    const validateStepOne = ({ username, email, password }) => {
        dispatch({ type: `${SLICE_NAME}/validateCredentials`, payload: { userName: username, email, password } });
        setRegistrationForm({ ...registrationForm, username, email, password });
    };

    const candidateAsAView = (candidate) => {
        const allowedKeysInOrder = [
            'email',
            'userType',
            'accountType',
            'applicantType',
            'representativeType',
            'legalStatus',
            'legalName',
            'firstName',
            'surName',
            'organizationName',
        ];

        const translatableFieldKeys = ['userType', 'accountType', 'applicantType', 'representativeType', 'legalStatus'];

        return allowedKeysInOrder.reduce((previous, current) => {
            return candidate[current]
                ? [...previous, translatableFieldKeys.includes(current) ? t(candidate[current]) : candidate[current]]
                : [...previous];
        }, []);
    };

    const showConfirmationPage = (accountFields) => {
        const firstForm = {
            email: registrationForm.email,
            password: registrationForm.password,
            username: registrationForm.email,
        };
        const mergedForms = { ...firstForm, ...accountFields };
        setRegistrationForm(mergedForms);
        setCandidateFieldsList(candidateAsAView(mergedForms));
        setStep('THIRD');
    };

    const registerUser = ({ privacyPolicy, gdprConsent }) => {
        setRegistrationForm({ ...registrationForm, privacyPolicy, gdprConsent });
        const { username, userType, ...restFields } = registrationForm;
        let otherFields = { ...restFields };
        if (otherFields.accountType) otherFields.accountType = account[otherFields.accountType];
        if (otherFields.legalStatus) otherFields.legalStatus = account[otherFields.legalStatus];

        dispatch({
            type: `${SLICE_NAME}/registerAccount`,
            payload: { ...otherFields, userName: username, personType: account[userType], language: defaultLanguage },
        });
    };

    const navigateToLogin = () => {
        history.push('/login');
    };

    const { accountType, userType, legalStatus } = registrationForm;
    const shouldConsent =
        (userType === 'applicant' && accountType === 'individuals') || (userType === 'representative' && legalStatus === 'individuals');

    const renderItem = (
        <>
            {step === 'FIRST' && <StepOne completeStepOne={validateStepOne} candidateUser={registrationForm} error={error} />}
            {step === 'SECOND' && (
                <StepTwo
                    backToStepOne={() => setStep('FIRST')}
                    proceedToVerification={showConfirmationPage}
                    accountTypes={accountTypes}
                    candidateUser={registrationForm}
                    legalStatusOptions={legalStatusOptions}
                />
            )}
            {step === 'THIRD' && (
                <StepThree
                    accountDetails={candidateFieldsList}
                    backToStepTwo={() => setStep('SECOND')}
                    consent={{ privacyPolicy: !!registrationForm.privacyPolicy, gdprConsent: !!registrationForm.gdprConsent }}
                    shouldConsent={shouldConsent}
                    register={registerUser}
                    recaptchaSiteKey={ipo.recaptchaKey}
                />
            )}
            {step === 'SUCCESS' && (
                <RequestResult
                    action={navigateToLogin}
                    message={'registration.success.message'}
                    messageVariables={{ email: registrationForm.email, ipoTitle: 'IPO Office' }}
                    button={'back.to.login'}
                />
            )}
        </>
    );

    return (
        <AuthContainer preTitle={2} title={t('create.new.account.user.area')}>
            {loading ? <CircularProgress /> : renderItem}
        </AuthContainer>
    );
};

export default SelfRegistrationPage;
